import { logger } from 'helpers/logger'

export type SerializedRequest = {
  url: string
  method: string
  headers: Record<string, string>
  body: string | null
  mode: RequestMode
  credentials: RequestCredentials
  cache: RequestCache
  redirect: RequestRedirect
  referrer: string
  integrity: string
}

export const serializeRequest = async (
  request: Request,
): Promise<SerializedRequest> => {
  const headers: Record<string, string> = {}
  request.headers.forEach((value, key) => {
    headers[key] = value
  })

  const getBody = async () => {
    try {
      return await request.text()
    } catch (error) {
      logger.error('No body or error reading body:', error)
      return null
    }
  }

  const body = ['get', 'head'].includes(request.method.toLowerCase())
    ? null
    : await getBody()

  return {
    body,
    headers,
    url: request.url,
    method: request.method,
    mode: request.mode,
    credentials: request.credentials,
    cache: request.cache,
    redirect: request.redirect,
    referrer: request.referrer,
    integrity: request.integrity,
  }
}

export const deserializeRequest = (data: SerializedRequest): Request => {
  const { url, method, headers, body, ...options } = data

  let requestHeaders = headers
  const hasJsonContentType =
    headers['content-type']?.includes('application/json')

  const canUpdateHeaders =
    (method === 'PUT' || method === 'POST' || method === 'DELETE') &&
    !hasJsonContentType

  if (canUpdateHeaders) {
    requestHeaders = {
      ...requestHeaders,
      'Content-Type': 'application/json',
    }
  }

  return new Request(url, {
    method,
    headers: requestHeaders,
    body,
    ...options,
  })
}

export type SerializedResponse = {
  url: string
  status: number
  statusText: string
  headers: Record<string, string>
  body: unknown
}

const getResponseBody = async (response: Response) => {
  try {
    const contentType = response.headers.get('content-type')

    const type = contentType?.split(';')[0].trim()

    switch (true) {
      case type?.includes('application/json'):
        return (await response.json()) as unknown

      case type?.includes('application/octet-stream'):
        return await response.arrayBuffer()

      case type?.includes('multipart/form-data'):
        return await response.formData()

      case type?.includes('image/'):
      case type?.includes('application/pdf'):
        return await response.blob()

      case type?.includes('text/'):
      default:
        return await response.text()
    }
  } catch (error) {
    logger.error('getResponseBody error', error)
    return null
  }
}

export const serializeResponse = async (
  response: Response,
): Promise<SerializedResponse> => {
  const headers: Record<string, string> = {}
  response.headers.forEach((value, key) => {
    headers[key] = value
  })

  return {
    url: response.url,
    status: response.status,
    statusText: response.statusText,
    headers: headers,
    body: await getResponseBody(response),
  }
}

export const deserializeResponse = (data: SerializedResponse): Response => {
  const { url, status, statusText, headers, body } = data

  const response = new Response(JSON.stringify(body), {
    status,
    statusText,
    headers,
  })

  Object.defineProperties(response, {
    url: {
      value: url,
    },
    type: {
      value: 'cors',
    },
  })

  return response
}
